import { useState } from "react";

import {
  useCreateDashboard,
  useDashboards,
  useDeleteDashboard,
  useDuplicateDashboard,
} from "@web-app/api";
import {
  Button,
  Drawer,
  Icon,
  Input,
  Tooltip,
  useAlert,
} from "@web-app/components";
import Navigation from "@web-app/pages/common/Navigation";
import type { CreateDashboardPayload, Dashboard } from "@web-app/types";

import { CreateDashboard } from "./CreateDashboard";
import { EditDashboard } from "./EditDashboard";
import TabTitle from "./TabTitle";

type AnalyticsPageProps = {
  logout: () => void;
};

export default function AnalyticsPage({ logout }: AnalyticsPageProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [showCreateDrawer, setShowCreateDrawer] = useState(false);
  const [selectedDashboard, setSelectedDashboard] = useState<Dashboard | null>(
    null,
  );
  const { showAlert } = useAlert();

  // Fetch dashboards using React Query
  const { data: dashboards = [], error, isLoading } = useDashboards();
  const createDashboardMutation = useCreateDashboard();
  const deleteDashboardMutation = useDeleteDashboard();
  const duplicateDashboardMutation = useDuplicateDashboard();

  const filteredDashboards = dashboards.filter((dashboard) => {
    const searchValue = searchTerm.toLowerCase();
    return (
      dashboard.title.toLowerCase().includes(searchValue) ||
      dashboard.description.toLowerCase().includes(searchValue)
    );
  });

  const handleCreateDashboard = () => {
    setShowCreateDrawer(true);
  };

  const handleCloseDrawer = () => {
    setShowCreateDrawer(false);
  };

  const handleAddDashboard = async (payload: CreateDashboardPayload) => {
    try {
      await createDashboardMutation.mutateAsync(payload);
      handleCloseDrawer();
      showAlert({
        message: "Dashboard created successfully",
        title: "Success",
        type: "success",
      });
    } catch (err) {
      console.error("Failed to create dashboard:", err);
      showAlert({
        message: "Failed to create dashboard. Please try again.",
        title: "Error",
        type: "error",
      });
    }
  };

  const handleDuplicateDashboard = async (dashboard: Dashboard) => {
    try {
      await duplicateDashboardMutation.mutateAsync(dashboard.id);
      showAlert({
        message: "Dashboard duplicated successfully",
        title: "Success",
        type: "success",
      });
    } catch (err) {
      console.error("Failed to duplicate dashboard:", err);
      showAlert({
        message: "Failed to duplicate dashboard. Please try again.",
        title: "Error",
        type: "error",
      });
    }
  };

  const handleDeleteDashboard = async (dashboardId: string) => {
    try {
      await deleteDashboardMutation.mutateAsync(dashboardId);
      showAlert({
        message: "Dashboard deleted successfully",
        title: "Success",
        type: "success",
      });
    } catch (err) {
      console.error("Failed to delete dashboard:", err);
      showAlert({
        message: "Failed to delete dashboard. Please try again.",
        title: "Error",
        type: "error",
      });
      throw err;
    }
  };

  return (
    <div className="h-screen min-h-screen max-w-full">
      <Navigation logout={logout} />

      <div className="flex-1 lg:pl-20 overflow-y-auto">
        <div className="py-10 lg:py-6">
          <div className="sticky top-0 z-20 bg-white">
            <TabTitle
              description="View and manage your analytics dashboards"
              icon="chartMixedLight"
              title="Analytics"
            />
          </div>

          <div className="px-6">
            <div className="sticky top-0 z-20 bg-white pb-6">
              <div className="flex items-center gap-3">
                <div className="flex-1">
                  <Input
                    className="w-full"
                    onChange={setSearchTerm}
                    placeholder="Search dashboards..."
                    type="search"
                    value={searchTerm}
                  />
                </div>
                <Button
                  className="flex items-center gap-2"
                  icon="plus"
                  onClick={handleCreateDashboard}
                  type="button"
                >
                  Create Dashboard
                </Button>
              </div>
            </div>

            {isLoading ? (
              <div className="flex items-center justify-center p-12">
                <div className="text-sm text-gray-500">
                  Loading dashboards...
                </div>
              </div>
            ) : error ? (
              <div className="flex flex-col items-center justify-center rounded-lg border bg-red-50 border-red-200 p-12">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
                  <Icon className="text-red-400" name="exclamationTriangle" />
                </div>
                <h3 className="mt-4 text-sm font-medium text-gray-900">
                  Error loading dashboards
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  Failed to load dashboards
                </p>
                <Button
                  className="mt-4"
                  color="light"
                  onClick={() => window.location.reload()}
                  type="button"
                >
                  Try Again
                </Button>
              </div>
            ) : filteredDashboards.length === 0 ? (
              <div className="flex flex-col items-center justify-center rounded-lg border bg-gray-50 border-gray-200 p-12">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
                  <Icon className="text-gray-400" name="chartMixedLight" />
                </div>
                <h3 className="mt-4 text-sm font-medium text-gray-900">
                  No dashboards found
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  {searchTerm
                    ? "Try adjusting your search"
                    : "Create your first dashboard to start visualizing your data"}
                </p>
                {!searchTerm && (
                  <Button
                    className="mt-4"
                    color="light"
                    onClick={handleCreateDashboard}
                    type="button"
                  >
                    Create Dashboard
                  </Button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {filteredDashboards.map((dashboard) => (
                  <div key={dashboard.id} className="group relative">
                    <a
                      className="rounded-lg border border-dashed border-gray-300 bg-white p-4 hover:border-gray-500 active:border-gray-500 transition-all duration-200 cursor-pointer block outline-none"
                      href={`/analytics/${dashboard.id}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm font-medium text-gray-900 truncate">
                            {dashboard.title}
                          </h3>
                          <Tooltip
                            className="!block w-full"
                            position="bottom"
                            showIcon={false}
                            text={dashboard.description}
                          >
                            <p className="mt-1 text-xs text-gray-500 line-clamp-2 min-h-8">
                              {dashboard.description}
                            </p>
                          </Tooltip>
                          <div className="mt-2 text-xs text-gray-400">
                            {dashboard.charts.length}{" "}
                            {dashboard.charts.length === 1 ? "chart" : "charts"}
                          </div>
                        </div>
                      </div>
                    </a>
                    <div className="absolute top-2 right-2 flex gap-1">
                      <button
                        className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-gray-100 rounded focus:outline-none focus:opacity-100"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleDuplicateDashboard(dashboard);
                        }}
                        title="Duplicate dashboard"
                        type="button"
                      >
                        <Icon
                          className="text-gray-400 hover:text-gray-600"
                          name="copy"
                        />
                      </button>
                      <button
                        className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-gray-100 rounded focus:outline-none focus:opacity-100"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setSelectedDashboard(dashboard);
                        }}
                        title="Edit dashboard"
                        type="button"
                      >
                        <Icon
                          className="text-gray-400 hover:text-gray-600"
                          name="fasPencil"
                        />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      <Drawer
        description=""
        icon="chartMixedLight"
        isOpen={showCreateDrawer}
        onClose={handleCloseDrawer}
        title="Create Dashboard"
      >
        <CreateDashboard
          onClose={handleCloseDrawer}
          onCreateDashboard={handleAddDashboard}
        />
      </Drawer>
      {selectedDashboard && (
        <Drawer
          description=""
          icon="fasPencil"
          isOpen={selectedDashboard !== null}
          onClose={() => setSelectedDashboard(null)}
          title="Edit Dashboard"
        >
          <EditDashboard
            dashboard={selectedDashboard}
            onClose={() => setSelectedDashboard(null)}
            onDelete={handleDeleteDashboard}
          />
        </Drawer>
      )}
    </div>
  );
}
